import { useMutation } from "@apollo/react-hooks";
import { useAsyncCallback } from 'react-use-async-callback';
import gql from "graphql-tag";
import { AsyncActionStatus } from '../../../shared/abstractStore';
import { ProfileUpdateInput } from '../generated/globalTypes';
import { updateProfileMutation, updateProfileMutationVariables } from '../generated/updateProfileMutation';
import { profileFieldsFragment } from '../models/Profile';

/**
 * Get a callback to update a Profile in the store.
 */
export function useUpdateProfileCallback(): [(id: string, model: ProfileUpdateInput) => Promise<void>, AsyncActionStatus] {
    const [mutationAction, { error }] = useMutation<updateProfileMutation, updateProfileMutationVariables>(
        gql`
            mutation updateProfileMutation ($model: ProfileUpdateInput!) {
                updateProfile(model: $model) {
                    ...profileFields
                }
            }

            ${profileFieldsFragment}
        `
    );

    // Wrap the mutation in a standard callback.
    const [action, { isExecuting, errors: actionErrors }] = useAsyncCallback(async (id: string, model: ProfileUpdateInput) => {
        const result = await mutationAction({ variables: { model: { ...model, id: id } } });
        if (result.errors && result.errors.length) {
            throw result.errors;
        }
    }, [mutationAction]);

    return [action, { isExecuting: isExecuting, errors: error ? error.message : actionErrors }];
}
